/**
 * plugin-client.ts — Generic ɳSentry plugin API client
 *
 * Purpose:    Reusable fetch wrapper for all 7 ɳSentry plugin backends (ports 3831-3837).
 *             Returns Result<T, SentryError> for typed error propagation to AsyncScreen.
 * Inputs:     Plugin port, path, optional auth token from env
 * Outputs:    Result<T, SentryError>
 * Constraints: Client-side only (Vite SPA); reads VITE_NSENTRY_AUTH_TOKEN env var;
 *             base URL override via VITE_NSENTRY_BASE for local dev. A loopback
 *             target (e.g. localhost, 127.0.0.1, [::1]) is refused, without calling
 *             fetch, unless the page itself is served from a loopback host
 *             (plugin-guard.ts, D-0898): a public site never probes a visitor.
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { ok, err, type Result } from './result'
import { errorFromResponse, errorFromException, type SentryError } from './sentry-error'
import { loopbackRefusedError, shouldRefuseTarget } from './plugin-guard'

const BASE = import.meta.env.VITE_NSENTRY_BASE ?? 'http://localhost'
const AUTH_TOKEN = import.meta.env.VITE_NSENTRY_AUTH_TOKEN ?? ''
const TIMEOUT_MS = 8_000

export function pluginUrl(port: number, path: string): string {
  return `${BASE}:${port}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Typed fetch against a plugin backend.
 * @param port  Plugin port (3831-3837)
 * @param path  API path
 * @param init  Optional RequestInit (method, body, etc.)
 */
export async function pluginFetch<T>(
  port: number,
  path: string,
  init?: RequestInit,
): Promise<Result<T, SentryError>> {
  const url = pluginUrl(port, path)
  if (shouldRefuseTarget(url, globalThis.location?.hostname ?? '')) {
    return err(loopbackRefusedError())
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const res = await fetch(url, {
      ...init,
      // A redirect could land on a loopback target the guard never saw.
      redirect: 'error',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
        ...(init?.headers ?? {}),
      },
    })
    clearTimeout(timeoutId)

    if (!res.ok) {
      const retryAfter = res.headers.get('Retry-After')
      return err(errorFromResponse(res, retryAfter ? Number(retryAfter) : undefined))
    }

    const data = (await res.json()) as T
    return ok(data)
  } catch (e) {
    clearTimeout(timeoutId)
    return err(errorFromException(e))
  }
}
